/**
 * @file homeApi.js
 * @description 首页域 API（入参/出参与存量保持一致）
 * @module features/home/api/homeApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { bannerDebug } from '@/features/home/utils/bannerDebug'

/**
 * @typedef {Object} HomeBannerComic
 * @property {number} position 格位 1~8
 * @property {string} comicId
 * @property {string} name
 * @property {string} coverUrl
 */

/**
 * @typedef {Object} HomeBannerFrame
 * @property {number} sort 屏排序
 * @property {HomeBannerComic[]} comics
 */

/**
 * @typedef {'WEB' | 'H5'} HomeBannerClientType
 */

/**
 * @typedef {Object} HomeBannerListData
 * @property {HomeBannerClientType} clientType
 * @property {string} clientTypeLabel
 * @property {string} templateType
 * @property {string} templateTypeLabel
 * @property {number} carouselIntervalMs
 * @property {HomeBannerFrame[]} frames
 */

/**
 * 首页漫画分类列表
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchHomeComicApi() {
  return axios.get(API.HOME.INFO)
}

/**
 * 归一化 Banner 列表响应（兼容 frames 在根级或 data 内）
 * @param {Record<string, unknown>} data
 */
function normalizeBannerListBody(data) {
  if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return { ...data, .../** @type {Record<string, unknown>} */ (data.data) }
  }
  return data
}

/**
 * @param {unknown} rawFrames
 * @returns {HomeBannerFrame[]}
 */
function parseBannerFrames(rawFrames) {
  if (!Array.isArray(rawFrames)) return []
  return rawFrames
    .map((frame) => ({
      sort: Number(frame.sort) || 0,
      comics: Array.isArray(frame.comics)
        ? frame.comics.map((comic) => ({
            position: Number(comic.position),
            comicId: comic.comicId ?? '',
            name: comic.name ?? '',
            coverUrl: comic.coverUrl ?? '',
          }))
        : [],
    }))
    .sort((a, b) => a.sort - b.sort)
}

/**
 * Banner 接口无数据时，用首页分类漫画生成回退轮播（保证 PC/H5 有轮播区）
 * @param {import('@/features/home/utils/bannerLink').HomeBannerClientType} clientType
 * @returns {Promise<HomeBannerFrame[]>}
 */
export async function fetchHomeBannerFallbackFramesApi(clientType) {
  const { data } = await fetchHomeComicApi()
  if (data.code !== 200 && data.code !== 0) return []

  const comicVoArray = data.data?.comicVoArray || []
  /** @type {Array<{ id: string|number, name?: string, cover?: string }>} */
  const allComics = []
  for (const cat of comicVoArray) {
    if (!cat?.infoVoArray) continue
    for (const comic of cat.infoVoArray) {
      if (comic?.id) allComics.push(comic)
    }
  }
  if (allComics.length === 0) return []

  if (clientType === 'H5') {
    return allComics.slice(0, 5).map((comic, idx) => ({
      sort: idx,
      comics: [
        {
          position: 1,
          comicId: String(comic.id),
          name: comic.name ?? '',
          coverUrl: comic.cover ?? '',
        },
      ],
    }))
  }

  const frames = []
  for (let i = 0; i < allComics.length; i += 8) {
    const chunk = allComics.slice(i, i + 8)
    frames.push({
      sort: frames.length,
      comics: chunk.map((comic, idx) => ({
        position: idx + 1,
        comicId: String(comic.id),
        name: comic.name ?? '',
        coverUrl: comic.cover ?? '',
      })),
    })
  }
  return frames.slice(0, 3)
}

/**
 * 首页 Banner 轮播列表
 * @param {import('@/features/home/utils/bannerLink').HomeBannerClientType} clientType WEB=PC 8 宫格，H5=移动端全宽单图
 * @returns {Promise<HomeBannerListData>}
 */
export async function fetchHomeBannerListApi(clientType) {
  const { data } = await axios.post(API.HOME.BANNER_LIST, { clientType })
  const body = normalizeBannerListBody(data)
  bannerDebug('api-raw', {
    clientType,
    code: body?.code,
    frameCount: Array.isArray(body?.frames) ? body.frames.length : -1,
    responseClientType: body?.clientType,
    templateType: body?.templateType,
  })
  if (body.code !== 200 && body.code !== 0) {
    throw new Error(body.msg ?? '获取 Banner 失败')
  }

  const frames = parseBannerFrames(body.frames)

  return {
    clientType: body.clientType === 'H5' ? 'H5' : 'WEB',
    clientTypeLabel: body.clientTypeLabel ?? '',
    templateType: body.templateType ?? '',
    templateTypeLabel: body.templateTypeLabel ?? '',
    carouselIntervalMs: Number(body.carouselIntervalMs) || 5000,
    frames,
  }
}